import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PortfolioMasterPage.css';
import { API_BASE_URL } from '../services/api';

const initialForm = {
  name: '',
  approver_type: 'Individual',
  designation: '',
  contact_number: '',
  address: '',
  approver_level: 'Checker',
  approver_limit: ''
};

export default function InvestmentApproverMasterPage() {
  const [form, setForm] = useState(initialForm);
  const [approvers, setApprovers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchApprovers();
  }, []);

  async function fetchApprovers() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/investment-approver-master`);
      setApprovers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError('Failed to load investment approvers');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/investment-approver-master/${editingId}`, form);
        setSuccess('Investment approver updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/investment-approver-master`, form);
        setSuccess('Investment approver created successfully');
      }
      setForm(initialForm);
      setEditingId(null);
      fetchApprovers();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save investment approver');
    }
  }

  function handleEdit(approver) {
    setForm({
      name: approver.name || '',
      approver_type: approver.approver_type || 'Individual',
      designation: approver.designation || '',
      contact_number: approver.contact_number || '',
      address: approver.address || '',
      approver_level: approver.approver_level || 'Checker',
      approver_limit: approver.approver_limit || ''
    });
    setEditingId(approver.id);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this investment approver?')) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(`${API_BASE_URL}/investment-approver-master/${id}`);
      fetchApprovers();
    } catch (e) {
      setError('Failed to delete investment approver');
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    setForm(initialForm);
    setEditingId(null);
    setError('');
    setSuccess('');
  }

  return (
    <div className="page-container ui-page">
      <h2 style={{ color: '#000', textAlign: 'center', marginBottom: '1.5rem', fontWeight: 'bold' }}>
        Investment Approver Master
      </h2>

      <form className="form-section compact-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#000', fontWeight: 500 }}>Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label style={{ color: '#000', fontWeight: 500 }}>Type</label>
            <select
              name="approver_type"
              value={form.approver_type}
              onChange={handleChange}
            >
              <option value="Committee">Committee</option>
              <option value="Individual">Individual</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{ color: '#000', fontWeight: 500 }}>Designation</label>
            <input
              name="designation"
              value={form.designation}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#000', fontWeight: 500 }}>Contact Number</label>
            <input
              name="contact_number"
              value={form.contact_number}
              onChange={handleChange}
            />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label style={{ color: '#000', fontWeight: 500 }}>Address</label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label style={{ color: '#000', fontWeight: 500 }}>Approver Level</label>
            <select
              name="approver_level"
              value={form.approver_level}
              onChange={handleChange}
            >
              <option value="Checker">Checker</option>
              <option value="Approver">Approver</option>
              <option value="Final Approver">Final Approver</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{ color: '#000', fontWeight: 500 }}>Approver Limit</label>
            <input
              type="number"
              step="0.01"
              name="approver_limit"
              value={form.approver_limit}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <button type="submit" className="save-verify-button" disabled={loading}>
            {editingId ? 'Update Approver' : 'Create Approver'}
          </button>
          {editingId && (
            <button
              type="button"
              className="action-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
      </form>

      <div className="transactions-section">
        <h3 style={{ color: '#000', fontWeight: 600 }}>Existing Investment Approvers</h3>
        {loading && <div>Loading...</div>}
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Designation</th>
              <th>Contact</th>
              <th>Level</th>
              <th>Limit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {approvers.map(a => (
              <tr key={a.id}>
                <td>{a.name}</td>
                <td>{a.approver_type}</td>
                <td>{a.designation}</td>
                <td>{a.contact_number}</td>
                <td>{a.approver_level}</td>
                <td>{a.approver_limit}</td>
                <td>
                  <button
                    className="action-button edit-button"
                    onClick={() => handleEdit(a)}
                  >
                    Edit
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => handleDelete(a.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {approvers.length === 0 && !loading && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  No investment approvers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

